import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { type Person } from 'types';
import { useIsSuperAdmin } from 'utils/models/User';
import {
  getBrowserTimezone,
  getTimezoneOption,
  type TimezoneOption,
} from 'utils/util/timezone';
import {
  getPeopleAdminableQuery,
  getPeopleOrAllQuery,
  INPUT_TYPES,
} from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import { SCHEDULE_INPUTS, type ScheduledParticipant } from './consts';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onAdd: (participants: ScheduledParticipant[]) => void;
  targetFilter?: Record<string, unknown> | null;
}

interface AddParticipantsFormData {
  people?: Person[];
  scheduled_time?: string | null;
  scheduled_timezone?: string | null;
}

const AddScheduledParticipantsModal: FC<Props> = ({
  isOpen,
  toggle,
  onAdd,
  targetFilter = null,
}) => {
  const { formatMessage } = useIntl();
  const isSuperAdmin = useIsSuperAdmin();

  const [formObj, setFormObj] = useState<AddParticipantsFormData>({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormObj({
      people: [],
      scheduled_time: null,
      scheduled_timezone: getTimezoneOption(
        getBrowserTimezone()
      ) as unknown as string,
    });
  }, [isOpen]);

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.PEOPLE_EDITOR,
        name: 'people',
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.add_participants.participants',
          defaultMessage: 'Participants',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.add_participants.participants_placeholder',
          defaultMessage: 'Add people',
        }),
        required: true,
        elasticsearchOptions: {
          // The survey's audience, read when the modal opens, narrows the
          // search on top of the caller's own scope.
          getQuery: (query: string) => ({
            ...(isSuperAdmin ? getPeopleOrAllQuery : getPeopleAdminableQuery)(
              query
            ),
            people_filter: targetFilter,
          }),
        },
      },
      ...SCHEDULE_INPUTS(formatMessage, {
        dateLabel: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.add_participants.scheduled_time_label',
          defaultMessage: 'When should they receive it?',
        }),
        minDate: 'today',
      }),
    ],
    [formatMessage, isSuperAdmin, targetFilter]
  );

  const handleChange = useCallback((newObj: AddParticipantsFormData) => {
    setFormObj(newObj);
  }, []);

  const transformObjectBeforeSubmit = useCallback(
    (data: AddParticipantsFormData): ScheduledParticipant[] => {
      const timezone =
        (data.scheduled_timezone as unknown as TimezoneOption)?.value ?? '';

      return (data.people ?? []).map((item) => {
        // The picker yields people directly, but a tag item carrying one under
        // `object` reads the same way.
        const person = ((item as { object?: Person }).object ?? item) as Person;
        return {
          person,
          person_id: person.id,
          scheduled_time: data.scheduled_time as string,
          scheduled_timezone: timezone,
          sent_at: null,
        };
      });
    },
    []
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.add_participants.title',
        defaultMessage: 'Add people',
      })}
      object={formObj}
      inputs={inputs}
      onChange={handleChange}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.custom_surveys.surveys.add_participants.submit',
        defaultMessage: 'Add',
      })}
      disableUnsavedChangesPrompt={true}
      triggerCallbackBeforeClose={true}
      callback={onAdd}
    />
  );
};

export default React.memo(AddScheduledParticipantsModal);
